import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { DateTimeRangePicker } from '../DateTimeRangePicker';
import { Button } from '../button';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { convert24To12Hour, zoneAbbreviation } from '../../helpers/timezoneHelpers';

const ModalOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-lg p-6 w-full max-w-2xl mx-4`}
`;

const ModalHeader = styled.div`
  ${tw`flex justify-between items-center mb-4`}
`;

const Title = styled.h2`
  ${tw`text-xl font-bold`}
`;

const CloseButton = styled.button`
  ${tw`text-text-secondary`}

  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const ButtonGroup = styled.div`
  ${tw`flex justify-end gap-4 mt-6`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

interface ModifyBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dates: {
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    startTimestamp: number;
    endTimestamp: number;
  }) => void;
  initialStartDate: Date;
  initialEndDate: Date;
  initialStartTime: string;
  initialEndTime: string;
  carId?: string;
  excludeBookingId?: string;
  timezone?: string | null;
}

export const ModifyBookingModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialStartDate,
  initialEndDate,
  initialStartTime,
  initialEndTime,
  carId,
  excludeBookingId,
  timezone,
}: ModifyBookingModalProps) => {
  const [selectedDates, setSelectedDates] = useState<any>(null);

  // 30-minute chips, as in the Flutter modify-trip sheet.
  const startAtOptions = Array.from({ length: 48 }, (_, i) =>
    convert24To12Hour(`${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`),
  );
  const endAtOptions = startAtOptions;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>Modify Return Date</Title>

          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <Subtitle>
          If you want to change pickup date and time, please cancel and book again.
          {timezone
            ? ` Return times are in the vehicle's local timezone (${zoneAbbreviation(timezone)}).`
            : ''}
        </Subtitle>

        <DateTimeRangePicker
          initialStartDate={initialStartDate}
          initialEndDate={initialEndDate}
          initialStartTime={initialStartTime}
          initialEndTime={initialEndTime}
          startAtOptions={startAtOptions}
          endAtOptions={endAtOptions}
          onDateTimeChange={dates => setSelectedDates(dates)}
          carId={carId}
          excludeBookingId={excludeBookingId}
          timezone={timezone}
          startDisabled
        />

        <ButtonGroup>
          <Button text="Cancel" theme="outlined" onClick={onClose} />
          <Button
            text="Confirm Changes"
            onClick={() => {
              if (selectedDates) {
                onConfirm(selectedDates);
                onClose();
              }
            }}
          />
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};
